import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SearchInput } from './search-input';
import { HttpMethod, HttpParams, RestService } from '../rest.service';
import { firstValueFrom } from 'rxjs';
import { CreateCustomer } from './create-customer';
import { Token } from './token';
import { jwtDecode } from 'jwt-decode';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Address, Customer, PagedCustomerResponse } from './customer';
import { logError } from '../helpers/sentry.helper';

// Configuration de base pour l'API
const BASE_URL = environment.castorama.webServices?.baseUrl;
const CLIENT_SECRET = environment.castorama.webServices?.clientSecret;
const CLIENT_ID = environment.castorama.webServices?.clientId;
const BEGIN_USERNAME = environment.castorama.webServices?.beginUsername;
const TENANT = environment.castorama.webServices?.tenant;
const API_VERSION = environment.castorama.webServices?.apiVersion;

// Constantes pour les messages d'erreur
const ERR_ACCOUNT_EXISTS = 'Un compte avec cette adresse mail existe déjà, veuillez vous connecter.';
const ERR_CREATION_FAILED = 'Une erreur est survenue lors de la création du compte';
const ERR_ADDRESS_CREATION_FAILED = "Une erreur est survenue lors de la création de l'adresse";
const ERR_AUTHENTICATION_FAILED = "Une erreur est survenue lors l'authentification de l'utilisateur";
const ERR_GET_ANON_TOKEN_FAILED = 'Impossible de récupérer le token anonyme';

@Injectable({
  providedIn: 'root',
})
export class KingfisherService extends RestService {
  // Points de terminaison API construits à partir des constantes
  private readonly _authBaseApi = `${BASE_URL}/oauth/oauth20`;
  private readonly _customerBaseApi: string;
  private readonly _colleagueBaseApi: string;

  constructor() {
    super();

    // Configuration dynamique des URLs de base selon l'environnement
    const environmentUrl = environment.castorama.webServices?.environmentUrl || '';

    this._customerBaseApi = `${BASE_URL}${environmentUrl}/${API_VERSION}/customers/${TENANT}`;
    this._colleagueBaseApi = `${BASE_URL}${environmentUrl}/colleague/${API_VERSION}/customers/${TENANT}`;
  }

  /**
   * Enregistre un nouveau client en tant qu'utilisateur anonyme
   * @param createCustomer Objet contenant les informations du client
   */
  async register(createCustomer: CreateCustomer): Promise<{ token: Token; customer: Customer }> {
    const address = createCustomer.address;
    delete createCustomer.address;
    try {
      const response = await this.makeApiCall({
        method: HttpMethod.POST,
        url: this._customerBaseApi,
        body: {
          data: {
            type: 'customerCreate',
            attributes: createCustomer,
          },
        },
        token: (await this.getAnonToken()).access_token,
      });

      const registeredToken = await this.login(createCustomer.email, createCustomer.password);

      //Si le client a saisi une adresse complète, on ajoute l'adresse, c'est une double sécurité normalement le form protège ce cas la
      if (this.validateAddress(address)) {
        // Ajout de l'adresse et authentification après la création
        createCustomer.address = address;

        const customerId = response.data.id;
        await this.createCustomerAddress(customerId, createCustomer, registeredToken.access_token);
      }

      const customer = await this.getCustomerInfo(registeredToken.access_token);

      return { token: registeredToken, customer };
    } catch (err) {
      logError(err);
      if (err?.status === 409) throw new Error(ERR_ACCOUNT_EXISTS);
      throw new Error(`${ERR_CREATION_FAILED} : ${err.detail}`);
    }
  }

  /**
   * Enregistre un client par un collègue avec un token d'authentification.
   * @param createCustomer Données du client
   * @param token Token du collègue
   */
  async registerByColleague(createCustomer: CreateCustomer, token: string): Promise<Customer> {
    const address = createCustomer.address;
    delete createCustomer.address;

    try {
      const responseCustomerCreate = await this.makeApiCall({
        method: HttpMethod.POST,
        url: `${this._colleagueBaseApi}/uninitializedCustomer`,
        body: {
          data: {
            type: 'uninitializedCustomerCreate',
            attributes: createCustomer,
          },
        },
        token,
        withCredentials: false,
      });

      const customerId = responseCustomerCreate.data.id;
      //Si le client a saisi une adresse complète, on ajoute l'adresse, c'est une double sécurité normalement le form protège ce cas la
      if (this.validateAddress(address)) {
        createCustomer.address = address;
        await this.createCustomerAddress(customerId, createCustomer, token);
      }

      return await this.getCustomerInfoByColleague(token, customerId);
    } catch (err) {
      logError(err);
      if (err?.status === 409) throw new Error(ERR_ACCOUNT_EXISTS);

      throw new Error(`${ERR_CREATION_FAILED} : ${err?.error?.errors[0].detail}`);
    }
  }

  /**
   * Authentifie un utilisateur avec email et mot de passe
   */
  async login(email: string, password: string): Promise<Token> {
    try {
      const token = await this.makeApiCall(
        {
          method: HttpMethod.POST,
          url: `${this._authBaseApi}/token`,
          body: new URLSearchParams({
            password,
            grant_type: 'password',
            client_secret: CLIENT_SECRET,
            client_id: CLIENT_ID,
            username: `${BEGIN_USERNAME}${TENANT}/${email}`,
          }).toString(),
        },
        {
          authorization: `Atmosphere atmosphere_app_id="${CLIENT_ID}"`,
          contentType: 'application/x-www-form-urlencoded',
        }
      );

      return plainToInstance(Token, token);
    } catch (err) {
      logError(err);
      throw new Error(ERR_AUTHENTICATION_FAILED);
    }
  }

  /**
   * Renouvelle un jeton client à partir de son `refresh_token` (OAuth grant_type `refresh_token`).
   * @param refreshToken Jeton de rafraîchissement issu du login initial.
   */
  async refresh(refreshToken: string): Promise<Token> {
    try {
      const token = await this.makeApiCall(
        {
          method: HttpMethod.POST,
          url: `${this._authBaseApi}/token`,
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_secret: CLIENT_SECRET,
            client_id: CLIENT_ID,
          }).toString(),
        },
        {
          authorization: `Atmosphere atmosphere_app_id="${CLIENT_ID}"`,
          contentType: 'application/x-www-form-urlencoded',
        }
      );

      return plainToInstance(Token, token);
    } catch (err) {
      logError(err);
      throw new Error(ERR_AUTHENTICATION_FAILED);
    }
  }

  /**
   * Récupère les informations d'un client avec un token utilisateur
   */
  async getCustomerInfo(token: string): Promise<Customer> {
    const decodedToken = jwtDecode(token);

    const result = await this.makeApiCall({
      method: HttpMethod.GET,
      url: `${this._customerBaseApi}/${decodedToken.sub}`,
      token,
    });

    return plainToInstance(Customer, result?.data?.attributes);
  }

  /**
   * Récupère les informations d'un client pour un collègue
   */
  async getCustomerInfoByColleague(token: string, customerKfId: string): Promise<Customer> {
    const result = await this.makeApiCall({
      method: HttpMethod.GET,
      url: `${this._colleagueBaseApi}/${customerKfId}`,
      token,
      withCredentials: false,
    });
    return plainToInstance(Customer, result?.data?.attributes);
  }

  /**
   * Envoie une requête pour réinitialiser le mot de passe
   */
  async forgottenPassword(email: string): Promise<void> {
    try {
      const token = await this.getAnonToken();
      return await this.makeApiCall({
        method: HttpMethod.POST,
        url: `${this._customerBaseApi}/forgottenPassword`,
        body: {
          data: {
            attributes: { email },
            type: 'forgottenPasswordInfo',
          },
        },
        token: token.access_token,
      });
    } catch (message) {
      logError(message);
      throw new Error(message);
    }
  }

  /**
   * Permet de retourner les marketingConsent du client
   * @param kfId
   * @param vendorToken
   */
  async fetchMarketingConsentData(kfId: string, vendorToken: string): Promise<any> {
    const filters = new URLSearchParams();
    filters.append('include', 'marketingConsent');

    try {
      const response = await this.makeApiCall({
        method: HttpMethod.GET,
        url: `${this._colleagueBaseApi}/${kfId}/?${filters.toString()}`,
        responseType: 'json',
        token: vendorToken,
        withCredentials: false,
      });
      return {
        marketingConsent:
          response?.included !== undefined ? response?.included[0].attributes?.marketingChannels : undefined,
      };
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Recherche des clients en fonction des critères donnés
   */
  async searchCustomers(searchInputs: SearchInput, vendorToken: string): Promise<PagedCustomerResponse> {
    const filters = new URLSearchParams();

    if (searchInputs.email) filters.append('filter[email]', this.normalizeValue(searchInputs.email));
    if (searchInputs.lastname) filters.append('filter[familyName]', this.normalizeValue(searchInputs.lastname));
    if (searchInputs.zipcode) filters.append('filter[postalCode]', this.normalizeValue(searchInputs.zipcode));
    if (searchInputs.phone) filters.append('filter[phoneNumber]', this.normalizeValue(searchInputs.phone));
    if (searchInputs.customerId) {
      filters.append('filter[customerNumber]', this.normalizeCustomerId(searchInputs.customerId));
    }
    if (searchInputs.fidelityId) {
      filters.append('filter[membershipNumber]', this.normalizeValue(searchInputs.fidelityId));
    }

    const size = '5';
    const page = Math.ceil((searchInputs.first + 1) / Number.parseInt(size));
    filters.append('page[number]', page.toString());

    filters.append('page[size]', size);

    filters.append('include', 'marketingConsent');

    try {
      const response = await this.makeApiCall({
        method: HttpMethod.GET,
        url: `${this._colleagueBaseApi}?${filters.toString()}`,
        responseType: 'json',
        token: vendorToken,
        withCredentials: false,
      });
      return {
        customers: response?.data?.map((c: any) => plainToInstance(Customer, c.attributes)) || [],
        meta: response?.meta || {
          totalSize: 0,
          pageNumber: 1,
          pageSize: size,
        },
        links: response?.links || {
          first: '',
          last: '',
          next: '',
          self: '',
        },
      };
    } catch (error) {
      logError(error);
      const errorCode = error?.error?.errors?.[0]?.code;
      if (errorCode === 'SEARCH_CRITERIA_TOO_WIDE') {
        throw {
          code: 999,
          description: 'Les résultats de votre recherche sont trop nombreux, merci de préciser',
        };
      } else if (errorCode === 'CUSTOMER_INVALID_EMAIL') {
        throw { code: 999, description: "L'email renseigné n'est pas valide" };
      }
      throw error;
    }
  }

  /**
   * Recherche des clients en fonction des critères donnés
   */
  async oldSearchCustomers(searchInputs: SearchInput, vendorToken: string): Promise<Customer[]> {
    const filters = new URLSearchParams();

    if (searchInputs.email) filters.append('filter[email]', this.normalizeValue(searchInputs.email));
    if (searchInputs.lastname) filters.append('filter[familyName]', this.normalizeValue(searchInputs.lastname));
    if (searchInputs.zipcode) filters.append('filter[postalCode]', this.normalizeValue(searchInputs.zipcode));
    if (searchInputs.phone) filters.append('filter[phoneNumber]', this.normalizeValue(searchInputs.phone));
    if (searchInputs.customerId) {
      filters.append('filter[customerNumber]', this.normalizeCustomerId(searchInputs.customerId));
    }
    if (searchInputs.fidelityId) {
      filters.append('filter[membershipNumber]', this.normalizeValue(searchInputs.fidelityId));
    }

    filters.append('page[number]', '1');
    filters.append('page[size]', '10');

    try {
      const response = await this.makeApiCall({
        method: HttpMethod.GET,
        url: `${this._colleagueBaseApi}?${filters.toString()}`,
        responseType: 'json',
        token: vendorToken,
        withCredentials: false,
      });

      return response?.data?.map((c: any) => plainToInstance(Customer, c.attributes)) || [];
    } catch (error) {
      logError(error);
      const errorCode = error?.error?.errors?.[0]?.code;
      if (errorCode === 'SEARCH_CRITERIA_TOO_WIDE') {
        throw {
          code: 999,
          description: 'Les résultats de votre recherche sont trop nombreux, merci de préciser',
        };
      } else if (errorCode === 'CUSTOMER_INVALID_EMAIL') {
        throw { code: 999, description: "L'email renseigné n'est pas valide" };
      }
      throw error;
    }
  }

  /**
   * Récupère un token anonyme pour effectuer des appels non authentifiés
   */
  private async getAnonToken(): Promise<Token> {
    try {
      const token = await this.makeApiCall(
        {
          method: HttpMethod.POST,
          url: `${this._authBaseApi}/token`,
          body: new URLSearchParams({
            password: 'ANON',
            grant_type: 'password',
            client_secret: CLIENT_SECRET,
            client_id: CLIENT_ID,
            username: `${BEGIN_USERNAME}${TENANT}/ANON`,
          }).toString(),
        },
        {
          authorization: `Atmosphere atmosphere_app_id="${CLIENT_ID}"`,
          contentType: 'application/x-www-form-urlencoded',
        }
      );

      return plainToInstance(Token, token);
    } catch (err) {
      logError(err);
      throw new Error(ERR_GET_ANON_TOKEN_FAILED);
    }
  }

  /**
   * Permet de vérifier si l'adresse est bonne : s'il y a la ville, le code postal, le pays, la rue et le numéro
   * @param address l'adresse à vérifier
   * @returns
   */
  private validateAddress(address: Address): boolean {
    return !(!address?.city || !address?.postalCode || !address?.country || !address.line1 || !address.num);
  }

  /**
   * Crée une adresse pour un client donné
   */
  private async createCustomerAddress(
    customerKfId: string,
    createCustomer: CreateCustomer,
    token: string
  ): Promise<any> {
    delete createCustomer.email;
    delete createCustomer.password;
    delete createCustomer.marketingChannels;
    delete createCustomer.dateOfBirth;

    try {
      const createCustomerInstance = plainToInstance(CreateCustomer, createCustomer);
      return await this.makeApiCall({
        method: HttpMethod.POST,
        url: `${environment.vendor ? this._colleagueBaseApi : this._customerBaseApi}/${customerKfId}/mainAddress`,
        token,
        body: {
          attributes: {
            contactDetails: {
              data: { attributes: instanceToPlain(createCustomerInstance) },
            },
          },
          type: 'contactInfo',
        },
        withCredentials: !environment.vendor,
      });
    } catch (err) {
      logError(err);
      throw new Error(ERR_ADDRESS_CREATION_FAILED);
    }
  }

  /**
   * Effectue un appel API avec les paramètres spécifiés
   */
  private async makeApiCall(
    params: HttpParams,
    options?: { authorization?: string; contentType?: string }
  ): Promise<any> {
    if (params.withCredentials !== false) params.withCredentials = true;

    return await firstValueFrom(
      this.restApi(params, {
        'Content-Type': options?.contentType || 'application/vnd.api+json',
        Authorization: options?.authorization || `Bearer ${params.token}`,
        'X-Tenant': TENANT,
      })
    );
  }

  private normalizeValue(val?: string): string {
    return val?.trim() ?? '';
  }

  private normalizeCustomerId(id?: string): string {
    if (!id) return '';
    return id.replace(/^SQ_/i, '').trim();
  }
}
